import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Image,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Spin,
  Empty
} from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  HeartOutlined,
  MessageOutlined,
  ShareAltOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const TikTokVideosDetail = ({ account, onBack }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (account) {
      loadVideos();
    }
  }, [account]);

  const loadVideos = async () => {
    if (!account) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Gọi API để lấy videos từ TikTok
          const response = await axios.get(`/api/tiktok-management/user/${account.username}/videos`, {
        params: {
          count: 50,
          cursor: 0
        },
        headers: {
          Authorization: `Bearer tiktok-ai-huydev-2025`
        }
      });

      if (response.data && response.data.data && response.data.data.videos) {
        setVideos(response.data.data.videos);
        message.success(`Đã tải ${response.data.data.videos.length} videos`);
      } else {
        setVideos([]);
        message.warning('Không tìm thấy videos nào');
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      message.error('Lỗi khi tải videos: ' + (error.response?.data?.detail || error.message));
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };


  const downloadVideo = async (videoUrl) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get('/api/tiktok-management/video/download', {
        params: { url: videoUrl, hd: 1 },
        headers: { Authorization: `Bearer tiktok-ai-huydev-2025` }
      });

      if (response.data && response.data.data) {
        // Open download link
        window.open(response.data.data.play, '_blank');
        message.success('Đang tải video...');
      }
    } catch (error) {
      message.error('Lỗi khi tải video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const columns = [
    {
      title: 'Thumbnail',
      dataIndex: 'cover',
      key: 'cover',
      width: 120,
      render: (cover, record) => (
        <div style={{ position: 'relative' }}>
          <Image
            src={cover || record.cover_url}
            alt="Video thumbnail"
            width={80}
            height={80}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
          <PlayCircleOutlined 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              fontSize: 24,
              color: 'white',
              textShadow: '0 0 4px rgba(0,0,0,0.8)'
            }} 
          />
        </div>
      ),
    },
    {
      title: 'Thông tin Video',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.title || record.desc || 'Không có tiêu đề'}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            ID: {record.id || record.video_id}
          </div>
          {record.duration && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Thời lượng: {Math.floor(record.duration / 60)}:{(record.duration % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thống kê',
      key: 'stats',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div style={{ fontSize: 12 }}>
            <EyeOutlined /> {record.play_count || record.playCount || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <HeartOutlined /> {record.digg_count || record.diggCount || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <MessageOutlined /> {record.comment_count || record.commentCount || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <ShareAltOutlined /> {record.share_count || record.shareCount || 0}
          </div>
        </Space>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<DownloadOutlined />}
            onClick={() => downloadVideo(record.play || record.video_url)}
          >
            Tải
          </Button>
          <Button 
            size="small" 
            onClick={() => window.open(record.play || record.video_url, '_blank')}
          >
            Xem
          </Button>
        </Space>
      ),
    },
  ];

  if (!account) {
    return (
      <Card>
        <Empty description="Không có thông tin account" />
      </Card>
    );
  }

  return (
    <div>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                Quay lại
              </Button>
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  Videos của @{account.username}
                </Title>
                <Text type="secondary">{account.nickname}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadVideos}
              loading={loading}
            >
              Tải lại
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Tổng Videos" 
              value={videos.length} 
              prefix={<PlayCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Followers" 
              value={account.follower_count} 
              prefix={<HeartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Following" 
              value={account.following_count} 
              prefix={<EyeOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Hearts" 
              value={account.heart_count} 
              prefix={<HeartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Videos Table */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={videos}
            rowKey={(record) => record.id || record.video_id || record.aweme_id}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} của ${total} videos`,
            }}
            locale={{
              emptyText: <Empty description="Không có videos nào" />
            }}
          />
        </Spin>
      </Card>

    </div>
  );
};

export default TikTokVideosDetail;